import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { Bell, Users } from "lucide-react";

interface PublishConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (sendNotifications: boolean) => void;
  version: string;
  title: string;
  alreadySentNotifications: boolean;
}

export const PublishConfirmationDialog = ({
  open,
  onOpenChange,
  onConfirm,
  version,
  title,
  alreadySentNotifications,
}: PublishConfirmationDialogProps) => {
  const [sendNotifications, setSendNotifications] = useState(!alreadySentNotifications);

  const handleConfirm = () => {
    onConfirm(sendNotifications);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Publish Release: {version}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>
              You are about to publish <strong>{title}</strong> and make it visible to all users.
            </p>
            
            {alreadySentNotifications ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-900 text-sm">
                <p className="font-medium">Notifications already sent</p>
                <p className="mt-1">Users have already been notified about this release. New notifications will not be sent even if the checkbox is enabled.</p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="send-notifications"
                    checked={sendNotifications}
                    onCheckedChange={(checked) => setSendNotifications(checked as boolean)}
                  />
                  <div className="flex-1 space-y-1">
                    <Label
                      htmlFor="send-notifications"
                      className="text-sm font-medium text-blue-900 cursor-pointer"
                    >
                      Send notifications to users
                    </Label>
                    <p className="text-xs text-blue-700">
                      <Users className="h-3 w-3 inline mr-1" />
                      This will create in-app notifications for all users and send emails to those subscribed to release notifications.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Once published, this release will appear on the Info page for all users.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            Publish Release
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
